import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ErrorApi } from '../lib/api'
import type { CatalogoBackoffice, Usuario } from '../lib/tipos'

const PESTAÑAS = [
  { codigo: 'catalogos', etiqueta: 'Catálogos' },
  { codigo: 'puntos', etiqueta: 'Tarifas y puntos' },
  { codigo: 'precios', etiqueta: 'Precios' },
  { codigo: 'textos', etiqueta: 'Textos legales' },
] as const

type Pestaña = (typeof PESTAÑAS)[number]['codigo']

const TIPOS_ITEM = ['final', 'objecion', 'motivo', 'sector', 'banco']
const FASES_FINAL = [
  'presentacion',
  'identificacion',
  'sondeo',
  'suministro',
  'objeciones',
  'oferta',
  'datos',
  'cierre',
]

export function Backoffice({ usuario }: { usuario: Usuario }) {
  const clienteQuery = useQueryClient()
  const [pestaña, setPestaña] = useState<Pestaña>('catalogos')
  const [aviso, setAviso] = useState<string | null>(null)

  const consulta = useQuery({
    queryKey: ['backoffice-catalogo'],
    queryFn: () => api<CatalogoBackoffice>('/api/backoffice/catalogo'),
  })

  const guardar = useMutation({
    mutationFn: ({ ruta, cuerpo }: { ruta: string; cuerpo: Record<string, unknown> }) =>
      api<CatalogoBackoffice>(`/api/backoffice/${ruta}`, { metodo: 'POST', cuerpo }),
    onSuccess: (datos) => {
      setAviso(null)
      clienteQuery.setQueryData(['backoffice-catalogo'], datos)
      // Lo que vean los comerciales también cambia.
      void clienteQuery.invalidateQueries({ queryKey: ['catalogos'] })
    },
    onError: (e) => setAviso(e instanceof ErrorApi ? e.message : 'No se ha podido guardar'),
  })

  if (consulta.isLoading || !consulta.data) {
    return <p className="px-4 py-10 text-center text-sm text-slate-400">Cargando el catálogo…</p>
  }

  const datos = consulta.data

  return (
    <div>
      <h1 className="mb-1 text-lg font-bold">Mantenimiento</h1>
      <p className="mb-4 text-sm text-slate-500">
        Lo que se cambie aquí les llega a los comerciales al momento, sin recargar, aunque
        estén en plena llamada.
      </p>

      <nav className="mb-5 flex flex-wrap gap-1">
        {PESTAÑAS.map((opcion) => (
          <button
            key={opcion.codigo}
            onClick={() => setPestaña(opcion.codigo)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
              pestaña === opcion.codigo
                ? 'bg-marca-700 text-white'
                : 'border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300'
            }`}
          >
            {opcion.etiqueta}
          </button>
        ))}
      </nav>

      {aviso && (
        <p className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {aviso}
        </p>
      )}

      {pestaña === 'catalogos' && <Catalogos datos={datos} guardar={guardar.mutate} />}
      {pestaña === 'puntos' && <Puntos datos={datos} guardar={guardar.mutate} usuario={usuario} />}
      {pestaña === 'precios' && <PreciosPanel datos={datos} guardar={guardar.mutate} />}
      {pestaña === 'textos' && <Textos datos={datos} guardar={guardar.mutate} />}
    </div>
  )
}

type Guardar = (variables: { ruta: string; cuerpo: Record<string, unknown> }) => void

const CAJA = 'rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900'
const ENTRADA =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-marca-500 focus:ring-2 focus:ring-marca-100 dark:border-slate-700 dark:bg-slate-950'
const BOTON = 'rounded-lg bg-marca-700 px-4 py-2 text-sm font-bold text-white transition hover:bg-marca-800'

function Catalogos({ datos, guardar }: { datos: CatalogoBackoffice; guardar: Guardar }) {
  const [tipo, setTipo] = useState('final')
  const [codigo, setCodigo] = useState('')
  const [etiqueta, setEtiqueta] = useState('')
  const [fase, setFase] = useState('')
  const [orden, setOrden] = useState('0')

  const items = datos.items.filter((item) => item.tipo === tipo)

  return (
    <div className="space-y-4">
      <div className={CAJA}>
        <div className="mb-4 flex flex-wrap gap-1">
          {TIPOS_ITEM.map((opcion) => (
            <button
              key={opcion}
              onClick={() => setTipo(opcion)}
              className={`rounded-lg px-3 py-1 text-xs font-medium transition ${
                tipo === opcion
                  ? 'bg-marca-50 text-marca-700 dark:bg-marca-700/20 dark:text-marca-100'
                  : 'text-slate-500'
              }`}
            >
              {opcion}
            </button>
          ))}
        </div>

        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {items.length === 0 && <li className="py-3 text-sm text-slate-400">Nada todavía.</li>}
          {items.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
              <span className="font-mono text-xs text-slate-400">{item.codigo}</span>
              <span className={item.activo ? '' : 'text-slate-400 line-through'}>{item.etiqueta}</span>
              {item.fase && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs dark:bg-slate-800">
                  {item.fase}
                </span>
              )}
              <button
                onClick={() =>
                  guardar({ ruta: `items/${item.id}/activar`, cuerpo: { activo: !item.activo } })
                }
                className="ml-auto text-xs text-slate-500 hover:underline"
              >
                {item.activo ? 'Desactivar' : 'Activar'}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <form
        className={CAJA}
        onSubmit={(e) => {
          e.preventDefault()
          guardar({
            ruta: 'items',
            cuerpo: { tipo, codigo, etiqueta, fase: tipo === 'final' ? fase : '', orden: Number(orden) },
          })
          setCodigo('')
          setEtiqueta('')
        }}
      >
        <h2 className="mb-3 text-sm font-bold">Añadir o cambiar en «{tipo}»</h2>
        <div className="grid gap-3 sm:grid-cols-4">
          <input value={codigo} onChange={(e) => setCodigo(e.target.value)} placeholder="código" className={ENTRADA} />
          <input
            value={etiqueta}
            onChange={(e) => setEtiqueta(e.target.value)}
            placeholder="lo que ve el comercial"
            className={`${ENTRADA} sm:col-span-2`}
          />
          <input value={orden} onChange={(e) => setOrden(e.target.value)} type="number" placeholder="orden" className={ENTRADA} />
        </div>
        {tipo === 'final' && (
          <select value={fase} onChange={(e) => setFase(e.target.value)} className={`${ENTRADA} mt-3`}>
            <option value="">Vale para cualquier fase</option>
            {FASES_FINAL.map((opcion) => (
              <option key={opcion} value={opcion}>
                Solo en {opcion}
              </option>
            ))}
          </select>
        )}
        <button type="submit" className={`${BOTON} mt-3`}>
          Guardar
        </button>
        <p className="mt-2 text-xs text-slate-400">
          Si el código ya existe, se actualiza. Nada se borra: se desactiva, porque hay
          llamadas que lo tienen apuntado.
        </p>
      </form>
    </div>
  )
}

function Puntos({
  datos,
  guardar,
  usuario,
}: {
  datos: CatalogoBackoffice
  guardar: Guardar
  usuario: Usuario
}) {
  const [tarifa, setTarifa] = useState(datos.tarifas[0]?.codigo ?? '')
  const [provincia, setProvincia] = useState('41')
  const [puntos, setPuntos] = useState('0')
  const [desde, setDesde] = useState(new Date().toISOString().slice(0, 10))

  const deTarifa = datos.puntos.filter((fila) => fila.tarifa_codigo === tarifa)

  return (
    <div className="space-y-4">
      <form
        className={CAJA}
        onSubmit={(e) => {
          e.preventDefault()
          guardar({ ruta: 'puntos', cuerpo: { tarifa, provincia, puntos: Number(puntos), desde } })
        }}
      >
        <h2 className="mb-1 text-sm font-bold">Cambiar puntos</h2>
        <p className="mb-3 text-xs text-slate-500">
          El cambio vale a partir del día que pongas. Lo anterior se queda como estaba, así
          que a los contratos ya activados no les afecta.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <select value={tarifa} onChange={(e) => setTarifa(e.target.value)} className={ENTRADA}>
            {datos.tarifas.map((fila) => (
              <option key={fila.codigo} value={fila.codigo}>
                {fila.compania} · {fila.nombre_visible} ({fila.codigo})
              </option>
            ))}
          </select>
          <select value={provincia} onChange={(e) => setProvincia(e.target.value)} className={ENTRADA}>
            {datos.provincias.map((fila) => (
              <option key={fila.codigo} value={fila.codigo}>
                {fila.codigo} · {fila.nombre}
              </option>
            ))}
          </select>
          <input value={puntos} onChange={(e) => setPuntos(e.target.value)} type="number" min="0" className={ENTRADA} />
          <input value={desde} onChange={(e) => setDesde(e.target.value)} type="date" className={ENTRADA} />
        </div>
        <button type="submit" className={`${BOTON} mt-3`}>
          Guardar
        </button>
      </form>

      {['supervisor', 'admin'].includes(usuario.rol) && <Recalcular />}

      <div className={CAJA}>
        <h2 className="mb-3 text-sm font-bold">Vigente y programado para {tarifa || '—'}</h2>
        <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
          {deTarifa.length === 0 && <li className="py-2 text-slate-400">Sin puntos todavía.</li>}
          {deTarifa.map((fila) => (
            <li key={`${fila.provincia_codigo}-${fila.desde}`} className="flex gap-4 py-2">
              <span className="font-mono text-xs text-slate-400">{fila.provincia_codigo}</span>
              <span className="font-semibold">{fila.puntos} puntos</span>
              <span className="ml-auto text-xs text-slate-500">
                desde {fila.desde}
                {fila.hasta !== '9999-12-31' && ` hasta ${fila.hasta}`}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/**
 * Después de cargar o corregir la tabla de puntos, los contratos que ya se
 * activaron siguen con los puntos que había entonces, que muchas veces es cero.
 */
function Recalcular() {
  const recalcular = useMutation({
    mutationFn: () =>
      api<{ revisados: number; cambiados: number; diferencia: number }>(
        '/api/contratos/recalcular-puntos',
        { metodo: 'POST', cuerpo: {} },
      ),
  })

  return (
    <div className={CAJA}>
      <h2 className="mb-1 text-sm font-bold">Recalcular contratos ya activados</h2>
      <p className="mb-3 text-xs text-slate-500">
        Cada contrato se recalcula con la tabla vigente <strong>el día en que se activó</strong>,
        no con la de hoy. Si su mes ya está liquidado, aquel mes no se toca: la diferencia entra
        como ajuste en el mes en curso.
      </p>
      <button
        onClick={() => recalcular.mutate()}
        disabled={recalcular.isPending}
        className={BOTON}
      >
        {recalcular.isPending ? 'Recalculando…' : 'Recalcular'}
      </button>

      {recalcular.data && (
        <p className="mt-3 text-sm">
          Revisados <strong>{recalcular.data.revisados}</strong>, cambiados{' '}
          <strong>{recalcular.data.cambiados}</strong>, diferencia{' '}
          <strong>{recalcular.data.diferencia.toLocaleString('es-ES')}</strong> puntos.
        </p>
      )}
      {recalcular.error && (
        <p className="mt-3 text-sm text-red-600">
          {recalcular.error instanceof ErrorApi ? recalcular.error.message : 'No se ha podido'}
        </p>
      )}
    </div>
  )
}

function PreciosPanel({ datos, guardar }: { datos: CatalogoBackoffice; guardar: Guardar }) {
  const [tarifa, setTarifa] = useState(datos.tarifas[0]?.codigo ?? '')
  const actual = datos.precios.find((fila) => fila.tarifa_codigo === tarifa)
  const [contenido, setContenido] = useState(actual?.contenido ?? '')
  const [ultimaTarifa, setUltimaTarifa] = useState(tarifa)

  // Al cambiar de tarifa se carga su hoja.
  if (tarifa !== ultimaTarifa) {
    setUltimaTarifa(tarifa)
    setContenido(actual?.contenido ?? '')
  }

  return (
    <form
      className={CAJA}
      onSubmit={(e) => {
        e.preventDefault()
        guardar({ ruta: 'precios', cuerpo: { tarifa, contenido } })
      }}
    >
      <h2 className="mb-1 text-sm font-bold">Hoja de precios</h2>
      <p className="mb-3 text-xs text-slate-500">
        Es lo que el comercial lee en voz alta en la fase de oferta. Si lo cambias a las
        siete de la mañana, a las nueve ya lo está leyendo bien.
      </p>
      <select value={tarifa} onChange={(e) => setTarifa(e.target.value)} className={`${ENTRADA} mb-3`}>
        {datos.tarifas.map((fila) => (
          <option key={fila.codigo} value={fila.codigo}>
            {fila.compania} · {fila.nombre_visible}
          </option>
        ))}
      </select>
      <textarea
        value={contenido}
        onChange={(e) => setContenido(e.target.value)}
        rows={10}
        placeholder="Precios y condiciones que hay que leer…"
        className={ENTRADA}
      />
      <button type="submit" className={`${BOTON} mt-3`}>
        Guardar
      </button>
      {actual && (
        <p className="mt-2 text-xs text-slate-400">Última actualización: {actual.actualizado_en}</p>
      )}
    </form>
  )
}

function Textos({ datos, guardar }: { datos: CatalogoBackoffice; guardar: Guardar }) {
  const codigos = [...new Set(datos.textos.map((texto) => texto.codigo))]
  const [codigo, setCodigo] = useState(codigos[0] ?? 'lopd')
  const versiones = datos.textos.filter((texto) => texto.codigo === codigo)
  const vigente = versiones[0]
  const [contenido, setContenido] = useState(vigente?.contenido ?? '')
  const [ultimoCodigo, setUltimoCodigo] = useState(codigo)

  if (codigo !== ultimoCodigo) {
    setUltimoCodigo(codigo)
    setContenido(vigente?.contenido ?? '')
  }

  return (
    <div className="space-y-4">
      <form
        className={CAJA}
        onSubmit={(e) => {
          e.preventDefault()
          guardar({ ruta: 'textos', cuerpo: { codigo, contenido } })
        }}
      >
        <h2 className="mb-1 text-sm font-bold">Texto legal</h2>
        <p className="mb-3 text-xs text-slate-500">
          Los textos no se corrigen: se guarda una versión nueva. En cada llamada queda
          apuntado qué versión se leyó, y eso es lo que os cubre.
        </p>
        <div className="mb-3 flex flex-wrap gap-2">
          <select value={codigo} onChange={(e) => setCodigo(e.target.value)} className={`${ENTRADA} sm:w-64`}>
            {codigos.map((opcion) => (
              <option key={opcion} value={opcion}>
                {opcion}
              </option>
            ))}
          </select>
          <input
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            placeholder="o uno nuevo"
            className={`${ENTRADA} sm:w-52`}
          />
        </div>
        <textarea
          value={contenido}
          onChange={(e) => setContenido(e.target.value)}
          rows={8}
          className={ENTRADA}
        />
        <button type="submit" className={`${BOTON} mt-3`}>
          Guardar versión nueva
        </button>
      </form>

      <div className={CAJA}>
        <h2 className="mb-3 text-sm font-bold">Versiones de «{codigo}»</h2>
        <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
          {versiones.map((texto, indice) => (
            <li key={texto.version} className="py-2">
              <span className="font-semibold">v{texto.version}</span>
              {indice === 0 && (
                <span className="ml-2 rounded-full bg-marca-50 px-2 py-0.5 text-xs text-marca-700">
                  vigente
                </span>
              )}
              <span className="ml-2 text-xs text-slate-400">{texto.vigente_desde}</span>
              <p className="mt-1 line-clamp-2 text-xs text-slate-500">{texto.contenido}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
